/**
 * Which JS chunks each public event tab needs on top of the persistent `(tabs)` shell, so the
 * shell can warm them in the browser cache before the viewer clicks.
 *
 * Why: tab links are not prefetched (prefetch multiplies function invocations), so on the first
 * visit of a tab the router learns the tab's client chunks only from the RSC response and then
 * downloads them: a second round trip after the server's answer (+170–330 ms on a throttled
 * phone). The chunk names are content hashes known only after `next build`, so the map is read
 * from the build's client reference manifests by a build-time static route
 * (`/api/event-tab-chunks`) and the shell fetches that file — a CDN file, not a function call.
 *
 * The manifest is a Next.js build artefact, not an API. Anything unrecognised yields an empty
 * map, and an empty map warms nothing: the tabs then load exactly as before.
 */

/** Route suffix after `/event/{id}/{slug}` ('' = overview, '/draw' = any draw) → chunk paths ('static/chunks/…'). */
export type TabChunkMap = Record<string, string[]>;

export const TAB_CHUNKS_PATH = '/api/event-tab-chunks';

const TABS_GROUP = '/[locale]/event/[eventId]/[slug]/(tabs)';
const MANIFEST_RE =
  /globalThis\.__RSC_MANIFEST\[("(?:[^"\\]|\\.)*")\]\s*=\s*(\{[\s\S]*\})\s*;?\s*$/;
const CHUNK_PATH_RE = /^static\/chunks\/[\w.~-]+\.js$/;
const CHUNK_SCRIPT_RE = /^(.*\/_next\/)static\/chunks\/[^/?#]+\.js(\?[^#]*)?$/;

/** One `page_client-reference-manifest.js`: its route key and entry JS files, or null. */
export function readClientReferenceManifest(
  source: string,
): { route: string; entryJSFiles: Record<string, string[]> } | null {
  const m = MANIFEST_RE.exec(source);
  if (!m) return null;
  try {
    const route: unknown = JSON.parse(m[1]);
    const body: unknown = JSON.parse(m[2]);
    const entry = (body as { entryJSFiles?: unknown } | null)?.entryJSFiles;
    if (typeof route !== 'string' || !entry || typeof entry !== 'object' || Array.isArray(entry)) {
      return null;
    }
    return { route, entryJSFiles: entry as Record<string, string[]> };
  } catch {
    return null;
  }
}

/** The tab a `(tabs)` page route serves, as its path suffix; null for anything that is not a tab. */
export function tabSuffixOfRoute(route: string): string | null {
  if (!route.startsWith(`${TABS_GROUP}/`) || !route.endsWith('/page')) return null;
  const rest = route.slice(TABS_GROUP.length, -'/page'.length);
  if (rest === '') return '';
  if (rest === '/draw/[drawId]') return '/draw';
  return /^\/[a-z]+$/.test(rest) ? rest : null;
}

/** Map every tab page among the manifests to the chunks it adds over the `(tabs)` layout. */
export function buildTabChunkMap(manifestSources: string[]): TabChunkMap {
  const map: TabChunkMap = {};
  for (const source of manifestSources) {
    if (typeof source !== 'string') continue;
    const manifest = readClientReferenceManifest(source);
    if (!manifest) continue;
    const suffix = tabSuffixOfRoute(manifest.route);
    if (suffix === null) continue;
    const entries = Object.entries(manifest.entryJSFiles);
    // Keys carry a build-specific project prefix ("[project]/src/app…"); match on the route tail.
    const page = entries.find(([key]) => key.endsWith(manifest.route))?.[1];
    if (!Array.isArray(page)) continue;
    const layout = entries.find(([key]) => key.endsWith(`${TABS_GROUP}/layout`))?.[1];
    const shell = new Set<unknown>(Array.isArray(layout) ? layout : []);
    map[suffix] = page.filter((file) => typeof file === 'string' && !shell.has(file));
  }
  return map;
}

/** The query (e.g. `?dpl=…`) the page's own chunk scripts carry; '' when none or no chunk script. */
export function chunkQueryOf(loadedScriptSrcs: string[]): string {
  for (const src of loadedScriptSrcs) {
    const m = CHUNK_SCRIPT_RE.exec(src);
    if (m) return m[2] ?? '';
  }
  return '';
}

/**
 * The routes worth warming from the shell: every listed tab, plus a draw (reached from Draws)
 * whenever Draws is listed. The open page's own chunks are already on the page and get skipped
 * by `chunkUrlsToWarm`.
 */
export function warmSuffixesFor(tabSuffixes: string[]): string[] {
  return tabSuffixes.includes('/divisions') ? [...tabSuffixes, '/draw'] : [...tabSuffixes];
}

/**
 * Absolute URLs to warm for `suffixes`, resolved like the page's own chunk scripts (same asset
 * prefix, same query such as a deployment pin) so the router's later load is a cache hit.
 * Skips chunks already on the page and repeats; empty when no chunk script is there to copy.
 */
export function chunkUrlsToWarm(
  map: TabChunkMap,
  suffixes: string[],
  loadedScriptSrcs: string[],
): string[] {
  const loaded = new Set<string>();
  let base: { prefix: string; query: string } | null = null;
  for (const src of loadedScriptSrcs) {
    const m = CHUNK_SCRIPT_RE.exec(src);
    if (!m) continue;
    base ??= { prefix: m[1], query: m[2] ?? '' };
    loaded.add(src.replace(/[?#].*$/, ''));
  }
  if (!base) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const suffix of suffixes) {
    const files: unknown = map[suffix];
    if (!Array.isArray(files)) continue;
    for (const file of files) {
      if (typeof file !== 'string' || !CHUNK_PATH_RE.test(file)) continue;
      const bare = `${base.prefix}${file}`;
      if (loaded.has(bare) || seen.has(bare)) continue;
      seen.add(bare);
      out.push(`${bare}${base.query}`);
    }
  }
  return out;
}
